import { useState, useEffect, useRef } from 'react';
import { collection, doc, documentId, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../../firebase';
import { markLive, unmarkLive } from '../lib/liveTickets';

/**
 * Subscribes to Firestore ticketChecks for each orderId.
 * Returns { [orderId]: checkData | null } — null once the server has confirmed
 * there is no doc yet; an id is absent until its data has arrived.
 *
 * ── Why batched ──────────────────────────────────────────────────────────────
 * This used to open ONE listener per order. Firestore advises fewer than 100
 * snapshot listeners per client; a busy day's board passed that on its own, and
 * with the long-polling transport (firebase.js) every listener is its own
 * target to set up — so the board took seconds to learn which tickets were
 * already done, longer the more tickets there were.
 *
 * Now the tickets on screen at mount are watched in `documentId() in [...]`
 * queries of up to CHUNK_SIZE ids (Firestore's cap for `in`): 61 tickets →
 * 3 listeners, one round trip each. The filter is on the document id, not on
 * `updatedAt`, on purpose: a doc whose serverTimestamp write is still pending
 * drops out of an `updatedAt`-range query until the server acks, which would
 * blank a ticket on every tap.
 *
 * ── Why new tickets don't join a batch ───────────────────────────────────────
 * Changing a batch's id list makes it a new query that re-reads every doc in
 * it. So tickets that arrive after mount get their own listener (1 read, as
 * before) until CHUNK_SIZE of them have built up; then they are sealed into one
 * new batch (their docs re-read once) and the per-ticket listeners are dropped
 * once that batch has synced. A whole batch's worth arriving at once (a wider
 * date range on Order Alerts) is batched straight away — the same one read each,
 * far fewer listeners. Tickets that leave the board keep their batch listening
 * (re-batching would re-read the rest) but it stops reporting them; a batch is
 * dropped once it reports none — e.g. yesterday's, after midnight. A ticket
 * that comes back gets a listener of its own, answered from the cache the old
 * batch kept warm, so its data always returns.
 *
 * Listeners report which tickets they keep live to lib/liveTickets, so
 * firestoreActions can read those from the local cache instead of the network.
 */
export const CHUNK_SIZE = 30;
const META = { includeMetadataChanges: true };   // to learn when a result is server-confirmed

/**
 * One listener over `ids`, with liveness bookkeeping. `onSynced` runs once, on
 * the first server-confirmed snapshot. Returns stop().
 */
function watch(ids, subscribe, apply, onSynced) {
  let synced = false;
  let stopped = false;
  const unsub = subscribe(
    (snap) => {
      if (stopped) return;
      apply(snap);
      if (!synced && !snap.metadata.fromCache) {
        synced = true;
        markLive(ids);
        if (onSynced) onSynced();
      }
    },
    () => { if (synced) { synced = false; unmarkLive(ids); } },
  );
  return () => {
    if (stopped) return;
    stopped = true;
    unsub();
    if (synced) { synced = false; unmarkLive(ids); }
  };
}

export function useTicketChecks(orderIds) {
  const [checks, setChecks] = useState({});
  // chunks: [{ ids, covers, stop }] — `covers` = the ids this batch still reports
  // (ids that left the board are dropped from it). tail: id → stop.
  const subsRef = useRef({ chunks: [], tail: new Map() });
  const wantRef = useRef(new Set());

  // The id SET is what matters, not the array identity — useLiveOrders hands us a
  // fresh array every poll with the same ids in it.
  const key = orderIds.join(',');

  useEffect(() => {
    // Falsy ids are skipped rather than passed to Firestore, which would throw;
    // duplicates would repeat a value in the `in` filter.
    const want = [...new Set(orderIds.filter(Boolean))];
    const wantSet = new Set(want);
    wantRef.current = wantSet;
    const s = subsRef.current;

    /**
     * Merge fresh data for tickets still on the board. Entries that didn't change
     * keep their objects (the board memoizes each card on its ticket's data), and
     * a patch that changes nothing keeps the whole map.
     */
    const put = (patch) => setChecks((prev) => {
      let next = null;
      Object.entries(patch).forEach(([id, v]) => {
        if (!wantRef.current.has(id)) return;
        if (id in prev && prev[id] === v) return;
        if (!next) next = { ...prev };
        next[id] = v;
      });
      return next || prev;
    });

    const addChunk = (ids, handoff = []) => {
      let pending = handoff;   // per-ticket listeners to drop once this batch is live
      const chunk = { ids, covers: new Set(ids), stop: null };
      const stopChunk = watch(
        ids,
        (next, err) => onSnapshot(query(collection(db, 'ticketChecks'), where(documentId(), 'in', ids)), META, next, err),
        (snap) => {
          const patch = {};
          // Only the docs that changed since this listener's last snapshot (all of
          // them the first time). Re-reading every doc would hand the board a new
          // object for all 30 tickets whenever one of them was tapped.
          snap.docChanges().forEach(({ type, doc: d }) => {
            if (type !== 'removed' && chunk.covers.has(d.id)) patch[d.id] = d.data();
          });
          // A doc missing from the result only means "no checks yet" once the
          // server has answered — from cache it may just not be cached.
          if (!snap.metadata.fromCache) {
            const present = new Set(snap.docs.map((d) => d.id));
            chunk.covers.forEach((id) => { if (!present.has(id)) patch[id] = null; });
          }
          put(patch);
        },
        () => { pending.forEach((stop) => stop()); pending = []; },
      );
      chunk.stop = () => { stopChunk(); pending.forEach((stop) => stop()); pending = []; };
      s.chunks.push(chunk);
    };

    const addOne = (id) => {
      s.tail.set(id, watch(
        [id],
        (next, err) => onSnapshot(doc(db, 'ticketChecks', id), META, next, err),
        (snap) => {
          if (snap.exists()) put({ [id]: snap.data() });
          else if (!snap.metadata.fromCache) put({ [id]: null });
        },
      ));
    };

    // 1. Let go of what no longer matters: per-ticket listeners for tickets that
    //    left, and batches that no longer report any ticket on the board. A batch
    //    that keeps some of its tickets stops reporting the ones that left.
    s.tail.forEach((stop, id) => {
      if (wantSet.has(id)) return;
      stop();
      s.tail.delete(id);
    });
    s.chunks = s.chunks.filter((c) => {
      c.covers.forEach((id) => { if (!wantSet.has(id)) c.covers.delete(id); });
      if (c.covers.size) return true;
      c.stop();
      return false;
    });

    // 2. Cover tickets nobody reports yet. Whole batches' worth are batched
    //    straight away; the rest (all of it when nothing is watched — first mount
    //    or a fresh day) one by one, until step 3 seals them.
    const covered = new Set(s.tail.keys());
    s.chunks.forEach((c) => c.covers.forEach((id) => covered.add(id)));
    const fresh = want.filter((id) => !covered.has(id));
    if (fresh.length) {
      const nothingWatched = s.chunks.length === 0 && s.tail.size === 0;
      const batched = nothingWatched ? fresh.length : fresh.length - (fresh.length % CHUNK_SIZE);
      for (let i = 0; i < batched; i += CHUNK_SIZE) addChunk(fresh.slice(i, Math.min(i + CHUNK_SIZE, batched)));
      fresh.slice(batched).forEach(addOne);
    }

    // 3. Seal a full tail of per-ticket listeners into one batch.
    while (s.tail.size >= CHUNK_SIZE) {
      const ids = [...s.tail.keys()].slice(0, CHUNK_SIZE);
      const stops = ids.map((id) => s.tail.get(id));
      ids.forEach((id) => s.tail.delete(id));
      addChunk(ids, stops);
    }

    // 4. Drop state for orders that left the board, so this map can't grow all day.
    setChecks((prev) => {
      let changed = false;
      const next = {};
      for (const id of Object.keys(prev)) {
        if (wantSet.has(id)) next[id] = prev[id]; else changed = true;
      }
      return changed ? next : prev;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Teardown belongs to the hook's lifetime, not to each id-set change — that is
  // what keeps a change from detaching listeners it is about to re-attach.
  useEffect(() => () => {
    const s = subsRef.current;
    s.chunks.forEach((c) => c.stop());
    s.tail.forEach((stop) => stop());
    subsRef.current = { chunks: [], tail: new Map() };
  }, []);

  return checks;
}
